/**
 * Teams API Integration
 */

import axiosClient from '../axiosClient';
import {
  Team,
  TeamMember,
  CreateTeamDto,
  UpdateTeamDto,
  AddTeamMemberDto,
  UpdateTeamMemberRoleDto,
} from '../types/TeamTypes';

export const teamsApi = {
  /**
   * List all teams in a workspace
   */
  getWorkspaceTeams: async (workspaceId: string): Promise<Team[]> => {
    const response = await axiosClient.get(`/workspaces/${workspaceId}/teams`);
    return response.data;
  },

  /**
   * Get a team by ID
   */
  getTeam: async (teamId: number): Promise<Team> => {
    const response = await axiosClient.get(`/teams/${teamId}`);
    return response.data;
  },

  /**
   * Create a team in a workspace
   */
  createTeam: async (workspaceId: string, data: CreateTeamDto): Promise<Team> => {
    const response = await axiosClient.post(`/workspaces/${workspaceId}/teams`, data);
    return response.data;
  },

  /**
   * Rename a team
   */
  updateTeam: async (teamId: number, data: UpdateTeamDto): Promise<Team> => {
    const response = await axiosClient.patch(`/teams/${teamId}`, data);
    return response.data;
  },

  /**
   * Delete a team
   */
  deleteTeam: async (teamId: number): Promise<void> => {
    await axiosClient.delete(`/teams/${teamId}`);
  },

  /**
   * Add a workspace member to a team
   */
  addMember: async (teamId: number, data: AddTeamMemberDto): Promise<TeamMember> => {
    const response = await axiosClient.post(`/teams/${teamId}/members`, data);
    return response.data;
  },

  /**
   * Update a team member's role
   */
  updateMemberRole: async (
    teamId: number,
    userId: number,
    data: UpdateTeamMemberRoleDto,
  ): Promise<TeamMember> => {
    const response = await axiosClient.patch(`/teams/${teamId}/members/${userId}`, data);
    return response.data;
  },

  /**
   * Remove a member from a team
   */
  removeMember: async (teamId: number, userId: number): Promise<void> => {
    await axiosClient.delete(`/teams/${teamId}/members/${userId}`);
  },

  /**
   * List the projects (boards) this team is assigned to. Assigning/
   * unassigning goes through boardsApi.assignTeam/unassignTeam (the join
   * row is the same either direction) - this is read-only.
   */
  getAssignedBoards: async (
    teamId: number,
  ): Promise<Array<{ id: number; name: string; key: string | null }>> => {
    const response = await axiosClient.get(`/teams/${teamId}/boards`);
    return response.data;
  },

  /**
   * Duplicate a team (and its members' roles) into another workspace. The
   * source team is untouched. Members not already in the target workspace
   * are added there automatically. Returns the newly created team.
   */
  copyToWorkspace: async (teamId: number, targetWorkspaceId: string): Promise<Team> => {
    const response = await axiosClient.post(`/teams/${teamId}/copy-to-workspace`, {
      targetWorkspaceId,
    });
    return response.data;
  },
};

export default teamsApi;
